/**
 * Iter 188.52 · Phase 3 — ProposalBuilderPanel
 *
 * Structural rewrite so section blocks read exactly like the AriaOne v6
 * mockup: top-hairline dividers (no boxed borders), inline uppercase
 * KIND tags, borderless rich-text areas with hover/focus ring, and a
 * grey `.autobox` panel inside the Pricing / Terms / Signatures blocks.
 *
 * Behaviour untouched — sections still drag-reorder, templates still
 * CRUD server-side, `cleanPaste()` still sanitizes on paste, all
 * data-testids preserved.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Plus, Trash2, ChevronDown, ChevronRight, GripVertical, Save } from "lucide-react";
import { toast } from "sonner";
import { cleanPaste, serializeSections } from "@/lib/proposalCleanPaste";
// Iter 188.54 — @dnd-kit sortable for the same "lift-and-drag" UX as
// the Quote line items. Whole-card grab, animated re-layout, keyboard
// accessible. Interactive elements inside the row are protected by a
// pointerdown guard so title inputs / toolbar buttons / editors keep
// working exactly as before.
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const _uid = () => (typeof crypto !== "undefined" && crypto.randomUUID
  ? crypto.randomUUID()
  : `ps-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`);

const _emptySection = () => ({ id: _uid(), title: "", html: "" });

// Scoped stylesheet — mirrors the mockup's `.block / .rt / .rtbar / .autobox / .mini`
// rules exactly. Injected once per panel mount so we don't leak globals but
// still benefit from CSS pseudo-classes (hover, focus, :empty::before).
const PANEL_CSS = `
  .qb-pp .qb-pp-block { border-top: 1px solid var(--qb-hair); padding: 14px 0; }
  .qb-pp .qb-pp-block:first-of-type { border-top: 0; padding-top: 6px; }
  .qb-pp .qb-pp-block > header { display: flex; align-items: center; gap: 9px; margin-bottom: 4px; }
  .qb-pp .qb-pp-title { flex: 1; border: 0; font-weight: 650; font-size: 13px; width: 100%;
    padding: 3px 6px; background: transparent; border-radius: 6px; color: var(--qb-ink);
    outline: none; transition: background 120ms ease; }
  .qb-pp .qb-pp-title:hover { background: #f3f6fa; }
  .qb-pp .qb-pp-title:focus { background: #f3f6fa; }
  .qb-pp .qb-pp-title:disabled { color: var(--qb-ink); background: transparent; }
  .qb-pp .qb-pp-kind { font-size: 9px; font-weight: 700; letter-spacing: 0.06em;
    text-transform: uppercase; color: var(--qb-faint); }
  .qb-pp .qb-pp-mv { border: 0; background: none; color: #cbd5e1; padding: 3px 5px;
    font-size: 12px; cursor: pointer; border-radius: 4px; }
  .qb-pp .qb-pp-mv:hover:not(:disabled) { color: var(--qb-ink-2); background: #f3f6fa; }
  .qb-pp .qb-pp-mv:disabled { opacity: 0.35; cursor: not-allowed; }
  .qb-pp .qb-pp-drag { cursor: grab; color: #cbd5e1; display: flex; align-items: center;
    padding: 2px; border-radius: 4px; }
  .qb-pp .qb-pp-drag:hover { color: var(--qb-ink-2); }

  .qb-pp .qb-pp-rt { padding: 6px 4px; font-size: 12.5px; line-height: 1.65;
    color: var(--qb-ink-2); outline: none; border-radius: 7px; min-height: 42px;
    transition: background 120ms ease, box-shadow 120ms ease; }
  .qb-pp .qb-pp-rt:hover { background: #fafbfd; }
  .qb-pp .qb-pp-rt:focus { background: #fff; box-shadow: 0 0 0 3px rgba(37,99,235,0.09); }
  .qb-pp .qb-pp-rt p { margin: 0 0 8px; }
  .qb-pp .qb-pp-rt ul, .qb-pp .qb-pp-rt ol { margin: 0 0 8px; padding-left: 20px; }
  .qb-pp .qb-pp-rt li { margin-bottom: 3px; }
  .qb-pp .qb-pp-rt h1, .qb-pp .qb-pp-rt h2, .qb-pp .qb-pp-rt h3, .qb-pp .qb-pp-rt h4 {
    margin: 10px 0 6px; color: var(--qb-navy); font-weight: 650; line-height: 1.3; }
  .qb-pp .qb-pp-rt h2 { font-size: 15px; }
  .qb-pp .qb-pp-rt h3 { font-size: 13.5px; }
  .qb-pp .qb-pp-rt table { border-collapse: collapse; width: 100%; margin: 8px 0; font-size: 12px; }
  .qb-pp .qb-pp-rt th, .qb-pp .qb-pp-rt td { border: 1px solid var(--qb-line);
    padding: 6px 8px; text-align: left; vertical-align: top; }
  .qb-pp .qb-pp-rt th { background: #f1f4f9; font-weight: 650; }
  .qb-pp .qb-pp-rt.qb-pp-paste:empty::before { content: attr(data-ph);
    color: var(--qb-faint); font-style: italic; }

  .qb-pp .qb-pp-rtbar { display: flex; gap: 4px; padding: 2px 0 8px; flex-wrap: wrap; }
  .qb-pp .qb-pp-rtbar button { border: 1px solid var(--qb-line); background: #fff;
    border-radius: 6px; min-width: 26px; height: 24px; color: var(--qb-muted);
    font-size: 11.5px; font-weight: 700; padding: 0 6px; cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center;
    transition: border-color 120ms ease, color 120ms ease; }
  .qb-pp .qb-pp-rtbar button:hover { border-color: #9dbcfb; color: var(--blue); }
  .qb-pp .qb-pp-rtbar .qb-pp-sep { width: 1px; background: var(--qb-hair);
    margin: 2px 3px; min-width: 1px; padding: 0; border: 0; height: 20px;
    align-self: center; }

  .qb-pp .qb-pp-autobox { background: #f7f9fc; border-radius: 10px; padding: 12px; }
  .qb-pp .qb-pp-autobox .qb-pp-hd { font-size: 11px; color: var(--qb-muted);
    font-weight: 600; margin-bottom: 8px; }

  .qb-pp .qb-pp-mini { width: 100%; border-collapse: collapse; font-size: 12.5px;
    background: #fff; border-radius: 8px; overflow: hidden; }
  .qb-pp .qb-pp-mini thead th { text-align: left; font-size: 9.5px;
    letter-spacing: 0.06em; text-transform: uppercase; color: var(--qb-faint);
    padding: 8px 10px; border-bottom: 1px solid var(--qb-hair); font-weight: 700;
    background: #fff; }
  .qb-pp .qb-pp-mini tbody td { padding: 9px 10px; border-bottom: 1px solid var(--qb-hair);
    vertical-align: top; line-height: 1.55; color: var(--qb-ink-2); }
  .qb-pp .qb-pp-mini tbody tr:last-child td { border-bottom: 0; }
  .qb-pp .qb-pp-mini td.first { font-weight: 600; color: var(--qb-ink); width: 44%; }
  .qb-pp .qb-pp-mini .right { text-align: right; font-variant-numeric: tabular-nums; }
  .qb-pp .qb-pp-mini tr.total td { font-weight: 700; color: var(--qb-navy);
    background: #f7f9fc; }

  .qb-pp .qb-pp-siglines { display: grid; grid-template-columns: 1fr 1fr; gap: 22px;
    padding: 4px 4px 0; font-size: 10.5px; color: var(--qb-muted); }
  .qb-pp .qb-pp-siglines .qb-pp-sigcol { border-top: 1px solid #cfd8e3; padding-top: 6px; }
  .qb-pp .qb-pp-siglines .qb-pp-sigcol b { color: var(--qb-ink-2); font-weight: 650; }
`;

export function ProposalBuilderPanel({
  included, onIncludedChange,
  sections, onSectionsChange,
  quoteHasLineItems,
  lineItems,
  terms,
  authHeaders,
}) {
  const [templates, setTemplates] = useState([]);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Iter 188.54 — @dnd-kit sensors. Pointer sensor with an 8px
  // activation distance keeps ordinary clicks inside titles/editor
  // areas from starting a drag. Keyboard sensor exposes Space + arrow
  // keys for accessible reordering.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    if (!included) return;
    axios.get(`${API}/proposal-templates`, { headers: authHeaders })
      .then((r) => setTemplates(r.data?.items || []))
      .catch(() => setTemplates([]));
  }, [included, authHeaders]);

  const addSection = () => onSectionsChange([...(sections || []), _emptySection()]);
  const removeSection = (id) => onSectionsChange((sections || []).filter((s) => s.id !== id));
  const updateSection = (id, patch) =>
    onSectionsChange((sections || []).map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const moveSection = (from, to) => {
    const arr = [...(sections || [])];
    if (from < 0 || from >= arr.length || to < 0 || to >= arr.length || from === to) return;
    const [it] = arr.splice(from, 1);
    arr.splice(to, 0, it);
    onSectionsChange(arr);
  };

  const handleSectionDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = (sections || []).findIndex((s) => s.id === active.id);
    const to = (sections || []).findIndex((s) => s.id === over.id);
    if (from < 0 || to < 0) return;
    moveSection(from, to);
  };

  const applyTemplate = (tplId) => {
    if (!tplId) return;
    const tpl = templates.find((t) => t.id === tplId);
    if (!tpl) return;
    const cloned = (tpl.sections || []).map((s) => ({ ...s, id: _uid() }));
    onSectionsChange(cloned.length > 0 ? cloned : [_emptySection()]);
    toast.success(`Loaded template · ${tpl.name}`);
  };

  const saveAsTemplate = async () => {
    const name = newTemplateName.trim();
    if (!name) {
      toast.error("Give the template a name first.");
      return;
    }
    const cleaned = serializeSections(sections);
    if (cleaned.length === 0) {
      toast.error("Add at least one section before saving.");
      return;
    }
    setSavingTemplate(true);
    try {
      const r = await axios.post(`${API}/proposal-templates`, {
        name, sections: cleaned, is_shared: true,
      }, { headers: authHeaders });
      setTemplates((prev) => [r.data, ...prev]);
      setSaveModalOpen(false);
      setNewTemplateName("");
      toast.success(`Saved template · ${name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  // ── Collapsed one-line summary strip ─────────────────────────
  if (!included) {
    const summary = (sections || []).length;
    return (
      <div
        data-testid="qb-proposal-collapsed"
        className="qb-paper"
        style={{
          padding: "10px 16px", marginBottom: 14,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <ChevronRight style={{ width: 14, height: 14, color: "var(--qb-muted)" }} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 650, color: "var(--qb-ink)" }}>Proposal</div>
            <div style={{ fontSize: 10, color: "var(--qb-muted)" }}>
              {summary > 0 ? `${summary} section${summary === 1 ? "" : "s"} · pricing synced` : "Off — PDF renders as a plain quote"}
            </div>
          </div>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, cursor: "pointer" }}>
          <input
            type="checkbox"
            data-testid="qb-proposal-include-toggle"
            checked={false}
            onChange={(e) => onIncludedChange(e.target.checked)}
          />
          Include
        </label>
      </div>
    );
  }

  // ── Expanded editor ──────────────────────────────────────────
  return (
    <div data-testid="qb-proposal-panel" className="qb-paper qb-pp" style={{ padding: "18px 20px", marginBottom: 14 }}>
      <style>{PANEL_CSS}</style>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ChevronDown style={{ width: 14, height: 14, color: "var(--qb-muted)" }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 650, color: "var(--qb-ink)", letterSpacing: "-0.01em" }}>Proposal</div>
            <div style={{ fontSize: 10, color: "var(--qb-muted)", marginTop: 1 }}>
              {sections?.length || 0} section{(sections?.length || 0) === 1 ? "" : "s"} · pricing synced
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={addSection}
            data-testid="qb-proposal-add-section"
            style={{
              border: 0, background: "none", color: "var(--blue)",
              fontWeight: 600, fontSize: 12.5, padding: "2px 0", cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 4,
            }}
          >
            <Plus style={{ width: 12, height: 12 }} /> Add section
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--qb-muted)" }}>
            <span>Start from</span>
            <select
              data-testid="qb-proposal-template-select"
              className="input"
              style={{ fontSize: 12, padding: "5px 8px", border: "1px solid var(--qb-line)", background: "#fff", borderRadius: 6, minWidth: 170 }}
              onChange={(e) => { applyTemplate(e.target.value); e.target.value = ""; }}
              defaultValue=""
            >
              <option value="">Blank</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name} (saved)</option>
              ))}
            </select>
            <button
              type="button"
              data-testid="qb-proposal-save-template"
              onClick={() => setSaveModalOpen(true)}
              style={{
                border: 0, background: "none", color: "var(--blue)",
                fontWeight: 600, fontSize: 12.5, cursor: "pointer", padding: 0,
                display: "inline-flex", alignItems: "center", gap: 4,
              }}
            >
              <Save style={{ width: 12, height: 12 }} /> Save as template
            </button>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, cursor: "pointer" }}>
            <input
              type="checkbox"
              data-testid="qb-proposal-include-toggle"
              checked={true}
              onChange={(e) => onIncludedChange(e.target.checked)}
            />
            Include
          </label>
        </div>
      </div>
      <div style={{ fontSize: 11, color: "var(--qb-faint)", padding: "4px 0 10px" }}>
        Sections print in this order. Pricing, Terms, and Signatures are generated and always sit last.
      </div>

      <DndContext
        sensors={dndSensors}
        collisionDetection={closestCenter}
        onDragEnd={handleSectionDragEnd}
      >
      <SortableContext
        items={(sections || []).map((s) => s.id)}
        strategy={verticalListSortingStrategy}
      >
      {(sections || []).map((s, i) => (
        <SortableProposalSection
          key={s.id}
          id={s.id}
          index={i}
          section={s}
          sectionCount={(sections || []).length}
          onTitleChange={(value) => updateSection(s.id, { title: value })}
          onHtmlChange={(html) => updateSection(s.id, { html })}
          onMoveUp={() => moveSection(i, i - 1)}
          onMoveDown={() => moveSection(i, i + 1)}
          onRemove={() => removeSection(s.id)}
        />
      ))}
      </SortableContext>
      </DndContext>

      {/* Iter 188.52 · Generated blocks styled as peer .qb-pp-block rows
          (top hairline + inline KIND pill) matching the mockup exactly.
          Body content sits inside a `.qb-pp-autobox` grey panel. */}
      <GeneratedPricingCard lineItems={lineItems || []} />
      <GeneratedTermsCard terms={terms} />
      <GeneratedSignatureCard />

      {saveModalOpen && (
        <div
          data-testid="qb-proposal-save-modal"
          style={{ position: "fixed", inset: 0, background: "rgba(15,41,66,0.55)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <div style={{ background: "#fff", borderRadius: 12, padding: 20, width: 420, boxShadow: "0 24px 60px rgba(0,0,0,0.25)" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--qb-ink)", marginBottom: 4 }}>Save proposal as template</div>
            <div style={{ fontSize: 11, color: "var(--qb-muted)", marginBottom: 12 }}>
              Reusable across the team. Sections and their content are copied — pricing is always derived per-quote.
            </div>
            <input
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              placeholder="Template name (e.g. Payment Posting SOW)"
              data-testid="qb-proposal-template-name"
              className="input"
              style={{ width: "100%", fontSize: 13, marginBottom: 12 }}
              autoFocus
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                type="button"
                onClick={() => { setSaveModalOpen(false); setNewTemplateName(""); }}
                disabled={savingTemplate}
                className="btn"
                style={{ fontSize: 11 }}
                data-testid="qb-proposal-template-cancel"
              >Cancel</button>
              <button
                type="button"
                onClick={saveAsTemplate}
                disabled={savingTemplate || !newTemplateName.trim()}
                className="btn qb-primary"
                style={{ fontSize: 11 }}
                data-testid="qb-proposal-template-save-confirm"
              >{savingTemplate ? "Saving…" : "Save template"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Zero-dependency contentEditable editor. Toolbar buttons execute
 * plain `document.execCommand`s — good enough for this scope and
 * keeps the bundle small. Word paste routes through `cleanPaste()`.
 */
function RichTextEditor({ html, onChange, testid }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (html || "")) {
      ref.current.innerHTML = html || "";
    }
  }, [html]);

  const exec = (cmd, arg) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    onChange(ref.current?.innerHTML || "");
  };

  const onPaste = (e) => {
    e.preventDefault();
    const dt = e.clipboardData;
    const htmlSrc = dt.getData("text/html");
    const textSrc = dt.getData("text/plain");
    const cleaned = htmlSrc
      ? cleanPaste(htmlSrc)
      : (textSrc || "").split(/\n{2,}/).map((p) => `<p>${_escape(p).replace(/\n/g, "<br/>")}</p>`).join("");
    document.execCommand("insertHTML", false, cleaned);
    onChange(ref.current?.innerHTML || "");
  };

  const onInput = () => onChange(ref.current?.innerHTML || "");

  return (
    <div>
      <div className="qb-pp-rtbar" data-testid={`${testid}-toolbar`}>
        <button type="button" title="Bold" onClick={() => exec("bold")} data-testid={`${testid}-bold`}><b>B</b></button>
        <button type="button" title="Italic" onClick={() => exec("italic")} data-testid={`${testid}-italic`}><i>I</i></button>
        <button type="button" title="Underline" onClick={() => exec("underline")} data-testid={`${testid}-underline`}><u>U</u></button>
        <span className="qb-pp-sep" />
        <button type="button" title="Bulleted list" onClick={() => exec("insertUnorderedList")} data-testid={`${testid}-ul`}>••</button>
        <button type="button" title="Numbered list" onClick={() => exec("insertOrderedList")} data-testid={`${testid}-ol`}>1.</button>
        <span className="qb-pp-sep" />
        <button type="button" title="Heading" onClick={() => exec("formatBlock", "H3")} data-testid={`${testid}-h`}>H</button>
        <button type="button" title="Body text" onClick={() => exec("formatBlock", "P")} data-testid={`${testid}-p`}>¶</button>
        <span className="qb-pp-sep" />
        <button type="button" title="Clear formatting" onClick={() => exec("removeFormat")} data-testid={`${testid}-clear`}>✕</button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={onInput}
        onPaste={onPaste}
        data-testid={testid}
        data-ph="Type here, or paste from Word — formatting, tables and lists are preserved."
        className="qb-pp-rt qb-pp-paste"
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Iter 188.52 · Generated blocks (Pricing / Terms / Signatures)
// Each renders as a peer `.qb-pp-block` — top hairline + inline
// KIND pill + `.qb-pp-autobox` grey panel body.
// ─────────────────────────────────────────────────────────────

const _MOCKUP_TERMS_DEFAULT =
  "Terms and Conditions included in the Service Agreements. Monthly Recurring " +
  "Charges will be billed upon the Go Live Date set forth in the Implementation " +
  "Deliverables Checklist. If the Client terminates service prior to the Service " +
  "End Date or before Service Start Date, Client shall immediately pay EHI any " +
  "implementation, setup, or training fees that were waived or discounted.";

function _fmtMoney(v) {
  const n = parseFloat(v || 0);
  if (!isFinite(n)) return "$0.00";
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function _GenBlockHeader({ title, kind = "generated" }) {
  return (
    <header>
      <span className="qb-pp-drag" style={{ opacity: 0.35 }} aria-hidden>
        <GripVertical style={{ width: 14, height: 14 }} />
      </span>
      <input value={title} disabled className="qb-pp-title" />
      <span className="qb-pp-kind">{kind}</span>
    </header>
  );
}

function GeneratedPricingCard({ lineItems }) {
  const priced = (lineItems || []).filter((li) => !(li?.kind === "heading" || li?.isHeading));
  const monthly = priced.reduce((acc, li) => {
    const b = (li?.basis || "fixed").toLowerCase();
    if (b === "annual" || b === "onetime" || b === "pctcoll" || b === "pctreimb") return acc;
    const t = parseFloat(li?.total) || 0;
    return acc + (isFinite(t) ? t : 0);
  }, 0);

  return (
    <div className="qb-pp-block" data-testid="qb-proposal-generated-pricing">
      <_GenBlockHeader title="Pricing" />
      <div className="qb-pp-autobox">
        <div className="qb-pp-hd">Generated from the line items — the only place a price is authored</div>
        {priced.length === 0 ? (
          <div style={{ fontSize: 11.5, color: "var(--qb-muted)", padding: "10px 4px", fontStyle: "italic" }}>
            Add line items below to see the pricing preview.
          </div>
        ) : (
          <table className="qb-pp-mini">
            <thead>
              <tr>
                <th>Line</th>
                <th className="right">Qty</th>
                <th className="right">Rate</th>
                <th className="right">Fee</th>
              </tr>
            </thead>
            <tbody>
              {priced.slice(0, 8).map((li, i) => (
                <tr key={i}>
                  <td className="first">{li.product || "—"}</td>
                  <td className="right">{li.quantity ?? 1}</td>
                  <td className="right">{_fmtMoney(li.unit_price)}</td>
                  <td className="right">{_fmtMoney(li.total)}</td>
                </tr>
              ))}
              {priced.length > 8 && (
                <tr>
                  <td colSpan={4} style={{ fontStyle: "italic", color: "var(--qb-muted)", fontSize: 11 }}>
                    + {priced.length - 8} more line{priced.length - 8 === 1 ? "" : "s"} …
                  </td>
                </tr>
              )}
              <tr className="total">
                <td className="first">Estimated monthly</td>
                <td></td>
                <td></td>
                <td className="right">{_fmtMoney(monthly)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function GeneratedTermsCard({ terms }) {
  const body = (terms || "").trim() || _MOCKUP_TERMS_DEFAULT;
  return (
    <div className="qb-pp-block" data-testid="qb-proposal-generated-terms">
      <_GenBlockHeader title="Terms" kind="terms" />
      <div className="qb-pp-autobox">
        <div style={{ fontSize: 12, color: "var(--qb-ink-2)", lineHeight: 1.65 }}>
          {body}
        </div>
      </div>
    </div>
  );
}

function GeneratedSignatureCard() {
  return (
    <div className="qb-pp-block" data-testid="qb-proposal-generated-signature">
      <_GenBlockHeader title="Signatures" />
      <div className="qb-pp-autobox">
        <div className="qb-pp-hd" style={{ marginBottom: 12 }}>
          Signature block — renders on the last page of the PDF
        </div>
        <div className="qb-pp-siglines">
          <div className="qb-pp-sigcol">
            <b>Accepted &amp; Agreed — Practice</b>
            <div style={{ marginTop: 2 }}>Signature · Printed name · Title · Date</div>
          </div>
          <div className="qb-pp-sigcol">
            <b>Enable Healthcare</b>
            <div style={{ marginTop: 2 }}>Signature · Printed name · Title · Date</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function _escape(s) {
  return String(s || "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Iter 188.54 — Sortable proposal section. Whole-card grab via
// @dnd-kit; the title input, toolbar buttons, and rich-text editor
// remain fully interactive thanks to a target-aware pointerdown guard.
function SortableProposalSection({
  id, index, section, sectionCount,
  onTitleChange, onHtmlChange, onMoveUp, onMoveDown, onRemove,
}) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id });

  const guardedListeners = {
    ...listeners,
    onPointerDown: (event) => {
      const t = event.target;
      if (t && t.closest && t.closest('input, select, textarea, button, label, [contenteditable="true"], [data-no-drag]')) {
        return;
      }
      listeners?.onPointerDown?.(event);
    },
    onKeyDown: (event) => {
      const t = event.target;
      if (t && t.closest && t.closest('input, select, textarea, [contenteditable="true"], [data-no-drag]')) {
        return;
      }
      listeners?.onKeyDown?.(event);
    },
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || "transform 180ms cubic-bezier(0.2, 0, 0, 1)",
    zIndex: isDragging ? 40 : "auto",
    position: isDragging ? "relative" : undefined,
    boxShadow: isDragging
      ? "0 18px 40px rgba(15, 41, 66, 0.22), 0 2px 6px rgba(15, 41, 66, 0.12)"
      : undefined,
    background: isDragging ? "#ffffff" : "transparent",
    borderRadius: isDragging ? 10 : undefined,
    cursor: isDragging ? "grabbing" : "grab",
    opacity: isDragging ? 0.98 : 1,
    touchAction: "manipulation",
  };

  return (
    <div
      ref={setNodeRef}
      className="qb-pp-block"
      style={style}
      data-testid={`qb-proposal-section-${index}`}
      data-dragging={isDragging ? "true" : undefined}
      {...attributes}
      {...guardedListeners}
    >
      <header>
        <span
          data-testid={`qb-proposal-drag-${index}`}
          className="qb-pp-drag"
          title="Drag to reorder — the whole section is grabbable"
          aria-hidden
          style={{ pointerEvents: "none" }}
        >
          <GripVertical style={{ width: 14, height: 14 }} />
        </span>
        <input
          value={section.title}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder={`Section ${index + 1} title (e.g. "Why this matters")`}
          data-testid={`qb-proposal-title-${index}`}
          className="qb-pp-title"
        />
        <span className="qb-pp-kind">rich text</span>
        <button
          type="button"
          onClick={onMoveUp}
          disabled={index === 0}
          data-testid={`qb-proposal-move-up-${index}`}
          className="qb-pp-mv"
          title="Move up"
        >↑</button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={index === sectionCount - 1}
          data-testid={`qb-proposal-move-down-${index}`}
          className="qb-pp-mv"
          title="Move down"
        >↓</button>
        <button
          type="button"
          onClick={onRemove}
          data-testid={`qb-proposal-remove-${index}`}
          className="qb-pp-mv"
          title="Remove section"
        >
          <Trash2 style={{ width: 12, height: 12 }} />
        </button>
      </header>
      <RichTextEditor
        html={section.html}
        onChange={onHtmlChange}
        testid={`qb-proposal-editor-${index}`}
      />
    </div>
  );
}

export default ProposalBuilderPanel;
